import i18next from 'i18next';
import moment, {type Moment} from 'moment-timezone';
import {useDispatch} from 'react-redux';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import {useFetchPricingGroupsQuery} from 'web-app/react/bookings-finance/hooks/use-fetch-pricing-groups-query';
import useTypedSelector from 'web-app/react/hooks/use-typed-selector';
import {useAllRuleGroups} from 'web-app/react/institution/settings/parent-booking/hooks/use-all-rule-groups';
import {useModal} from 'web-app/react/modal-context/modal-context';
import {useGetAutomaticPricingForPurchases} from 'web-app/react/profile/child-profile/plans-and-invoices/ad-hoc-purchases/hooks/use-get-automatic-pricing-for-purchases';
import type {Child as PowerPageChild} from 'web-app/react/routes/account/institution/finance/power-page/types';
import {checkIfFlagIsToggled, DebugLocalStorageFlags} from 'web-app/react/routes/debugger/local-storage-config';
import * as GlobalEvent from 'web-app/services/global-event';
import {captureMessage} from 'web-app/services/sentry';
import {filter} from 'web-app/util/array';
import * as Failable from 'web-app/util/failable';
import {pipe} from 'web-app/util/fp';
import {head, isNonEmpty, type NonEmptyArray} from 'web-app/util/non-empty-array';
import * as Warnable from 'web-app/util/warnable';
import type {FetchPlanWarningsResponse} from 'web-app/api/clients/rest-types/fetch-plan-warnings-for-child/fetch-plan-warnings-for-child-query';

import {EditBillingPlan} from '../edit-plan/loader';
import {getLatestPlan, sortPlansByLatest} from '../helpers/dates';
import {setUpLegacyPlanModal} from '../helpers/set-up-legacy-plan-modal';
import {SimplifiedUSBillingPlanModal} from '../loader';
import {transformApiPlanToMaybeAlreadyBilledPlan} from '../transformers/plan-transformers';
import {
    transformPowerPageChildToChild,
    patchUnbilledDatesIntoPlans,
} from '../transformers/power-page-child-transformer';
import type * as Types from '../types';
import {useCanAccessSimplifiedUSBillingPlan} from './use-can-access-simplified-us-billing-plan';
import {useFetchPlanWarnings} from './use-fetch-plan-warnings';
import {getAutomaticRuleGroupId} from '../helpers/rule-groups';

type Args = {
    afterSubmission?: Types.AfterSubmission;
    siteId: string;
    allChildren: Array<PowerPageChild> | undefined;
};

type CreatePlanFromTemplate = {
    templates?: Array<Types.Template>;
    selectedTemplateId?: string;
    handleAddPlanTemplatesModalClose?: VoidFunction;
};
export type ViewOptions =
    | {
          view: Extract<Types.View, 'edit'>;
          planId: string;
          childLocalStartDate: string;
      }
    | ({
          view: Extract<Types.View, 'create'>;
          subView: Types.CreateSubView;
      } & CreatePlanFromTemplate);

export type SimplifiedUSBillingPlanModalOpen = (
    selectedChildIds: NonEmptyArray<string>,
    viewOptions: ViewOptions,
) => void;

export type Result =
    | {isLoading: true}
    | {
          isLoading: false;
          open: SimplifiedUSBillingPlanModalOpen;
      };

export const useSimplifiedUSBillingPlanModal = ({siteId, allChildren, afterSubmission}: Args): Result => {
    const {
        isSimplifiedUsBillingPlanEnabled,
        isSimplifiedUsEditBillingPlanEnabled,
        isLoading: isSimplifiedUsBillingPlanAccessLoading,
    } = useCanAccessSimplifiedUSBillingPlan(siteId);
    const {
        automaticPricingForPurchasesData: {automaticPricing: automaticPricings, loading: isAutomaticPricingLoading},
    } = useGetAutomaticPricingForPurchases(siteId, allChildren?.map(child => child.child.id) ?? []);
    const {ruleGroupOptions, loading: isRuleGroupOptionsLoading} = useAllRuleGroups(siteId, true);

    const {fetchPlanWarnings, isWarningsLoading} = useFetchPlanWarnings();

    const {data: pricingGroupsData, loading: isPricingGroupsLoading} = useFetchPricingGroupsQuery({
        variables: {
            institutionSetId: siteId,
            institutionSetIds: [siteId],
        },
    });

    const reduxState = useTypedSelector(state => state);
    const dispatchReduxAction = useDispatch();

    const isLoading =
        isSimplifiedUsBillingPlanAccessLoading ||
        isAutomaticPricingLoading ||
        isRuleGroupOptionsLoading ||
        isWarningsLoading ||
        isPricingGroupsLoading;

    const {show: showCreatePlanModal, hide: hideCreatePlanModal} = useModal({
        component: SimplifiedUSBillingPlanModal,
        modalProps: {
            fullScreenOnMobile: true,
            noPadding: true,
        },
        modalStyle: {
            maxWidth: '100%',
            height: '100%',
            margin: '0px',
            borderRadius: '0px',
        },
        contentFullHeight: true,
        ignoreOutsideClickSelectors: ['.MuiInputBase-root'],
    });

    const {show: showEditPlanModal, hide: hideEditPlanModal} = useModal({
        component: EditBillingPlan,
        modalProps: {
            fullScreenOnMobile: true,
            noPadding: true,
        },
        modalStyle: {
            maxWidth: '100%',
            height: '100%',
            margin: '0px',
            borderRadius: '0px',
        },
        contentFullHeight: true,
        ignoreOutsideClickSelectors: ['.MuiInputBase-root'],
    });

    if (isLoading) {
        return {isLoading};
    }

    const canShowSimplifiedUSBillingPlanModal = checkIfFlagIsToggled(
        DebugLocalStorageFlags.SIMPLIFIED_US_BILLING_PLAN.flag,
    );

    const isEnabled = canShowSimplifiedUSBillingPlanModal || isSimplifiedUsBillingPlanEnabled;
    const isEditEnabled = canShowSimplifiedUSBillingPlanModal || isSimplifiedUsEditBillingPlanEnabled;

    return {
        open: (selectedChildIds: NonEmptyArray<string>, viewOptions: ViewOptions) => {
            const transformedChildrenAndCustomDiscounts = (() => {
                const childrenList = hasValue(allChildren)
                    ? Warnable.resolve(allChildren)
                    : Warnable.warningMessage([], 'No available children');

                if (Warnable.isWarnings(childrenList)) {
                    throw new Error(childrenList.warnings[0].warning);
                }

                const childrenToTransform =
                    viewOptions.view === 'edit'
                        ? childrenList.value.filter(child => child.child.id === head(selectedChildIds))
                        : childrenList.value;

                const transformedChildrenAndCustomDiscounts = Failable.map(
                    Failable.all(
                        childrenToTransform.map(child =>
                            transformPowerPageChildToChild({
                                child,
                                automaticPricings,
                                automaticRuleGroupId: getAutomaticRuleGroupId(ruleGroupOptions),
                            }),
                        ),
                    ),
                    filter(hasValue),
                );

                return dispatchFailuresToSentry('Failed to transform children', transformedChildrenAndCustomDiscounts);
            })();

            if (!isNonEmpty(transformedChildrenAndCustomDiscounts) || !isNonEmpty(selectedChildIds)) {
                return genericError();
            }

            const fetchFirstUnbilledDates = (): Promise<Map<string, Moment>> => {
                if (viewOptions.view === 'create' && viewOptions.subView === 'single') {
                    return fetchPlanWarnings({
                        variables: {childId: head(selectedChildIds)},
                    }).then(response => {
                        if (!hasValue(response?.data)) {
                            return new Map();
                        }
                        return getFirstUnbilledDatesForPlanIds(response.data);
                    });
                }
                return Promise.resolve(new Map());
            };

            const patchChildren = (
                children: {
                    child: Types.Child;
                    customDiscounts: Types.DiscountCustom[];
                }[],
                fetchFirstUnbilledDates: Map<string, Moment>,
            ): {
                child: Types.Child;
                customDiscounts: Types.DiscountCustom[];
            }[] =>
                children.map(pair => ({
                    ...pair,
                    child: patchUnbilledDatesIntoPlans(pair.child, fetchFirstUnbilledDates),
                }));

            const {handleLegacyPlanModalOpen, handleLegacyEditPlanModalOpen} = setUpLegacyPlanModal({
                dispatchReduxAction,
                reduxState,
                siteId,
                selectedChildIds,
                pricingGroupsData,
            });

            switch (viewOptions.view) {
                case 'create': {
                    if (isEnabled) {
                        return fetchFirstUnbilledDates().then(firstUnbilledDates =>
                            showCreatePlanModal({
                                onClose: hideCreatePlanModal,
                                afterSubmission,
                                initiallySelectedChildIds: selectedChildIds,
                                allChildren: patchChildren(
                                    transformedChildrenAndCustomDiscounts,
                                    firstUnbilledDates,
                                ).map(({child}) => child),
                                siteId,
                                automaticRuleGroupId: getAutomaticRuleGroupId(ruleGroupOptions),
                                automaticPricings,
                                selectedTemplate:
                                    viewOptions.templates?.find(
                                        template => template.id === viewOptions.selectedTemplateId,
                                    ) ?? null,
                                handleAddPlanTemplatesModalClose: viewOptions.handleAddPlanTemplatesModalClose,
                                handleLegacyPlanModalOpen,
                                subView: viewOptions.subView,
                            }),
                        );
                    }
                    return handleLegacyPlanModalOpen();
                }
                case 'edit': {
                    if (isEditEnabled && isEnabled) {
                        return fetchPlanWarnings({
                            variables: {childId: head(selectedChildIds)},
                        }).then(({data}) => {
                            if (!hasValue(data)) {
                                throw new Error('No plans available');
                            }

                            const plans = data.result.plans;

                            const transformedPlans = plans.map(transformApiPlanToMaybeAlreadyBilledPlan);

                            const maybeSelectedChildAndDiscounts = findChildAndDiscounts(
                                transformedChildrenAndCustomDiscounts,
                                head(selectedChildIds),
                            );
                            if (!maybeSelectedChildAndDiscounts.ok) {
                                throw new Error(maybeSelectedChildAndDiscounts.error);
                            }

                            const maybeInitialPlan = findPlanForChild(
                                maybeSelectedChildAndDiscounts.value.child,
                                viewOptions.planId,
                            );

                            if (!maybeInitialPlan.ok) {
                                throw new Error(maybeInitialPlan.error);
                            }

                            const initialStartDate = getInitialStartDate(maybeInitialPlan.value, transformedPlans);

                            return showEditPlanModal({
                                onClose: hideEditPlanModal,
                                siteId,
                                automaticRuleGroupId: getAutomaticRuleGroupId(ruleGroupOptions),
                                automaticPricings,
                                initialPlan: maybeInitialPlan.value,
                                initialStartDate: initialStartDate.initialStartDate,
                                firstUnbilledDate: initialStartDate.firstUnbilledDate,
                                selectedChild: maybeSelectedChildAndDiscounts.value.child,
                                customDiscounts: maybeSelectedChildAndDiscounts.value.customDiscounts,
                                afterSubmission,
                                handleLegacyEditPlanModalOpen: () =>
                                    handleLegacyEditPlanModalOpen(viewOptions.childLocalStartDate, viewOptions.planId),
                            });
                        });
                    }
                    return handleLegacyEditPlanModalOpen(viewOptions.childLocalStartDate, viewOptions.planId);
                }
                default:
                    return genericError();
            }
        },
        isLoading,
    };
};

const findPlanForChild = (child: Types.Child, planId: string): Failable.Failable<Types.Plan, string> => {
    const plan = child.plans.find(
        plan => (plan.type === 'plan' || plan.type === 'unsupported-plan') && plan.id === planId,
    );

    if (!plan) {
        return Failable.fail('Plan not found');
    }

    return Failable.resolve(plan);
};

const findChildAndDiscounts = (
    childrenAndDiscounts: Array<{child: Types.Child; customDiscounts: Types.DiscountCustom[]}>,
    childId: string,
): Failable.Failable<{child: Types.Child; customDiscounts: Types.DiscountCustom[]}, string> => {
    const childAndDiscount = childrenAndDiscounts.find(child => child.child.id === childId);

    if (!childAndDiscount) {
        return Failable.fail('Child not found');
    }

    return Failable.resolve(childAndDiscount);
};

const genericError = (): void => {
    GlobalEvent.updateError(i18next.t('somethingWentWrong'));
    captureMessage(`Failed opening Chill Bill modal`);
};

const dispatchFailuresToSentry = <TV, TW>(title: string, failable: Failable.Failable<TV, TW>): TV => {
    if (Failable.isFailed(failable)) {
        captureMessage(`${title}: ${failable.error}`);
        throw new Error(String(failable.error));
    }
    return failable.value;
};

export const getInitialStartDate = (
    initialPlan: Types.Plan,
    plans: Array<Types.MaybeAlreadyBilledPlan>,
): {initialStartDate: Types.InitialStartDate; firstUnbilledDate: Moment | null} => {
    const currentPlanStartDate = moment(initialPlan.startDate);
    const currentPlanEndDate = initialPlan.endDate ? moment(initialPlan.endDate) : null;
    const currentAlreadyBilledPlan = pipe(
        plans,
        filter(plan => plan.type === 'already-billed'),
        filter(plan => plan.effectiveChangeDate.isAfter(currentPlanStartDate)),
        sortPlansByLatest,
        getLatestPlan,
    );
    const nextPlan = pipe(
        plans,
        filter(plan => plan.startDate.isAfter(currentPlanEndDate)),
        sortPlansByLatest,
        getLatestPlan,
    );

    if (hasValue(currentAlreadyBilledPlan)) {
        if (currentAlreadyBilledPlan.effectiveChangeDate.isAfter(currentPlanEndDate) && hasValue(nextPlan)) {
            return {
                initialStartDate: {
                    type: 'full-plan-billed',
                    date: currentAlreadyBilledPlan.effectiveChangeDate,
                },
                firstUnbilledDate: currentAlreadyBilledPlan.effectiveChangeDate,
            };
        }
        return {
            initialStartDate: {
                type: 'current-plan-effective-date',
                date: currentAlreadyBilledPlan.effectiveChangeDate,
            },
            firstUnbilledDate: currentAlreadyBilledPlan.effectiveChangeDate,
        };
    }

    const previousPlan = pipe(
        plans,
        filter(plan => plan.startDate.isBefore(currentPlanStartDate)),
        sortPlansByLatest,
        getLatestPlan,
    );
    if (hasValue(previousPlan)) {
        if (previousPlan.type === 'already-billed') {
            return {
                initialStartDate: {type: 'previous-plan-effective-date', date: previousPlan.effectiveChangeDate},
                firstUnbilledDate: null,
            };
        }
        return {
            initialStartDate: {type: 'previous-plan-start-date', date: previousPlan.startDate},
            firstUnbilledDate: null,
        };
    }

    return {
        initialStartDate: {type: 'current-plan-start-date', date: currentPlanStartDate},
        firstUnbilledDate: null,
    };
};

/**
 * Generates a map from plan ids to first unbilled dates for plans with billed periods.
 *
 * The first unbilled date is the first date for a billed plan that has no billed periods after it. I.e. the day after the last billed period ends.
 */
const getFirstUnbilledDatesForPlanIds = (warnings: FetchPlanWarningsResponse): Map<string, Moment> => {
    const pairs = warnings.result.plans
        .map((plan): [string, Moment] | null => {
            const firstUnbilledDate = plan.behaviors.find(behavior => behavior.id === 'ShowPlanWarnings')?.payload
                ?.warnings[0].metadata?.changeFrom;
            if (!hasValue(firstUnbilledDate)) {
                return null;
            }
            return [plan.id, moment(firstUnbilledDate)];
        })
        .filter(hasValue);
    return new Map(pairs);
};
